import { useEffect, useState } from "react";
import { Archive, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/hooks/useTranslation";
import type { StrengthExercise } from "../types";

interface ExerciseManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exercises: StrengthExercise[];
  isSaving: boolean;
  onCreate: (data: { nazov: string; poradie: number }) => Promise<void>;
  onUpdate: (
    id: number,
    data: { nazov?: string; poradie?: number; aktivny?: boolean }
  ) => Promise<void>;
}

function ExerciseRow({
  exercise,
  isSaving,
  onUpdate,
}: {
  exercise: StrengthExercise;
  isSaving: boolean;
  onUpdate: ExerciseManagerDialogProps["onUpdate"];
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(exercise.nazov);
  const [order, setOrder] = useState(String(exercise.poradie));

  useEffect(() => {
    setName(exercise.nazov);
    setOrder(String(exercise.poradie));
  }, [exercise]);

  const hasChanges =
    name.trim() !== exercise.nazov || Number(order) !== exercise.poradie;

  return (
    <div className="grid items-end gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1fr)_96px_auto_auto]">
      <div className="space-y-2">
        <Label>{t("strengthProgress:fields.exercise")}</Label>
        <Input value={name} onChange={event => setName(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>{t("strengthProgress:fields.order")}</Label>
        <Input
          min="0"
          type="number"
          value={order}
          onChange={event => setOrder(event.target.value)}
        />
      </div>
      <Button
        disabled={isSaving || !hasChanges || name.trim().length === 0}
        size="sm"
        type="button"
        variant="outline"
        onClick={() =>
          onUpdate(exercise.id, {
            nazov: name.trim(),
            poradie: Number(order) || 0,
          })
        }
      >
        <Check className="mr-2 h-4 w-4" />
        {t("strengthProgress:saveExercise")}
      </Button>
      <Button
        disabled={isSaving}
        size="sm"
        type="button"
        variant={exercise.aktivny ? "outline" : "default"}
        onClick={() => onUpdate(exercise.id, { aktivny: !exercise.aktivny })}
      >
        <Archive className="mr-2 h-4 w-4" />
        {exercise.aktivny
          ? t("strengthProgress:archive")
          : t("strengthProgress:restore")}
      </Button>
    </div>
  );
}

export function ExerciseManagerDialog({
  open,
  onOpenChange,
  exercises,
  isSaving,
  onCreate,
  onUpdate,
}: ExerciseManagerDialogProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");
  const [newOrder, setNewOrder] = useState("0");

  useEffect(() => {
    if (!open) return;
    setNewName("");
    setNewOrder(
      String(Math.max(-1, ...exercises.map(exercise => exercise.poradie)) + 1)
    );
  }, [open, exercises]);

  const addExercise = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newName.trim()) return;
    await onCreate({ nazov: newName.trim(), poradie: Number(newOrder) || 0 });
    setNewName("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("strengthProgress:exerciseManager.title")}
          </DialogTitle>
          <DialogDescription>
            {t("strengthProgress:exerciseManager.description")}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid items-end gap-3 rounded-lg bg-muted/50 p-4 sm:grid-cols-[minmax(0,1fr)_96px_auto]"
          onSubmit={addExercise}
        >
          <div className="space-y-2">
            <Label htmlFor="new-strength-exercise">
              {t("strengthProgress:exerciseManager.newExercise")}
            </Label>
            <Input
              id="new-strength-exercise"
              maxLength={120}
              value={newName}
              onChange={event => setNewName(event.target.value)}
              placeholder={t(
                "strengthProgress:exerciseManager.newExercisePlaceholder"
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-strength-exercise-order">
              {t("strengthProgress:fields.order")}
            </Label>
            <Input
              id="new-strength-exercise-order"
              min="0"
              type="number"
              value={newOrder}
              onChange={event => setNewOrder(event.target.value)}
            />
          </div>
          <Button disabled={isSaving || !newName.trim()} type="submit">
            <Plus className="mr-2 h-4 w-4" />
            {t("strengthProgress:exerciseManager.addExercise")}
          </Button>
        </form>
        <div className="space-y-3">
          {exercises.map(exercise => (
            <ExerciseRow
              key={exercise.id}
              exercise={exercise}
              isSaving={isSaving}
              onUpdate={onUpdate}
            />
          ))}
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t("common:close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
